import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toFullUrl } from '../../config/api';
import { ShoppingBag, ShoppingCart, ChevronRight } from 'lucide-react';
import CommentSection from '../CommentSection';
import LikeButton from '../LikeButton';
import FireButton from '../FireButton';

export function MerchSection({ merchItems, siteSettings, onMerchBuy, isVisible }) {
  const [showAllMerch, setShowAllMerch] = useState(false);
  if (!isVisible('merch') || merchItems.length === 0) return null;

  return (
    <section className="border-t border-[#262626] py-16 sm:py-24 px-4 sm:px-8 bg-[#0A0A0A]" data-testid="merch-section" id="merch">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="overline text-[var(--primary,#FF3B30)] mb-2">{siteSettings?.section_titles?.merch_label || 'OFFICIAL'}</p>
            <h2 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter">
              {siteSettings?.section_titles?.merch || 'MERCH'}
            </h2>
          </div>
          <ShoppingBag className="w-8 h-8 text-[var(--primary,#FF3B30)]" />
        </div>
        <Link to="/store" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--accent,#DAA520)] hover:text-white transition-colors mb-8" data-testid="merch-store-link">
          <ShoppingBag className="w-4 h-4" /> Visit Full Store <ChevronRight className="w-4 h-4" />
        </Link>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {merchItems.slice(0, showAllMerch ? merchItems.length : 6).map(item => (
            <div key={item.id} className={`bg-[#141414] border border-[#262626] overflow-hidden group relative ${!item.in_stock ? 'opacity-80' : ''}`} data-testid={`merch-${item.id}`}>
              <div className="aspect-square bg-[#0A0A0A] overflow-hidden relative">
                {item.image ? (
                  <img src={toFullUrl(item.image)} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-12 h-12 text-[#262626]" /></div>
                )}
                {!item.in_stock && (
                  <div className="absolute top-3 left-3 bg-[#0A0A0A]/90 border border-[var(--primary,#FF3B30)] px-3 py-1">
                    <span className="text-xs font-bold uppercase text-[var(--primary,#FF3B30)] tracking-wider">Out of Stock</span>
                  </div>
                )}
                {item.on_sale && item.in_stock && (
                  <div className="absolute top-3 left-3 bg-[var(--accent,#DAA520)] px-3 py-1">
                    <span className="text-xs font-bold uppercase text-black tracking-wider">Sale</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-cabinet font-bold uppercase">{item.title}</h3>
                {item.gildan_style && <p className="text-[10px] text-[var(--accent,#DAA520)] font-mono mt-0.5">{item.gildan_style}</p>}
                {item.description && <p className="text-[#A3A3A3] text-sm mt-1 line-clamp-2">{item.description}</p>}
                {item.fabric_details && <p className="text-[10px] text-[#555] mt-1">{item.fabric_details}</p>}
                {item.sizes?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {item.sizes.map(s => (
                      <span key={s} className="px-1.5 py-0.5 text-[9px] font-bold border border-[#333] text-[#888]">{s}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between mt-4">
                  {item.purchasable && (
                    <>
                      {item.on_sale && item.sale_price != null ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-[#A3A3A3] line-through">${item.price.toFixed(2)}</span>
                          <span className="font-mono text-lg font-bold text-[var(--accent,#DAA520)]">${item.sale_price.toFixed(2)}</span>
                        </div>
                      ) : (
                        <span className="font-mono text-lg font-bold text-[var(--primary,#FF3B30)]">${item.price.toFixed(2)}</span>
                      )}
                    </>
                  )}
                  {!item.purchasable && <span />}
                  {item.purchasable && item.in_stock ? (
                    <button onClick={() => onMerchBuy(item)}
                      className="btn-success px-5 py-2.5 text-sm"
                      data-testid={`buy-merch-${item.id}`}>
                      <ShoppingCart className="w-4 h-4" /> BUY NOW
                    </button>
                  ) : item.purchasable && !item.in_stock ? (
                    <span className="px-4 py-2 bg-[#262626] text-[#666] text-sm font-bold uppercase cursor-not-allowed"
                      data-testid={`sold-out-${item.id}`}>
                      SOLD OUT
                    </span>
                  ) : null}
                </div>
                <CommentSection itemType="merch" itemId={item.id} />
                <div className="mt-2 flex items-center gap-4">
                  <LikeButton itemType="merch" itemId={item.id} />
                  <FireButton itemType="merch" itemId={item.id} />
                </div>
              </div>
            </div>
          ))}
        </div>
        {merchItems.length > 3 && !showAllMerch && (
          <div className="text-center mt-8">
            <button onClick={() => setShowAllMerch(true)}
              className="px-8 py-3 border-2 font-bold uppercase tracking-wider text-sm hover:bg-white hover:text-black transition-all"
              style={{ borderColor: 'var(--accent, #DAA520)', color: 'var(--accent, #DAA520)' }}
              data-testid="merch-see-more-btn">
              SEE ALL MERCH ({merchItems.length} items)
            </button>
          </div>
        )}
        {showAllMerch && merchItems.length > 3 && (
          <div className="text-center mt-8">
            <button onClick={() => setShowAllMerch(false)}
              className="px-6 py-2 text-xs font-bold uppercase tracking-wider text-[#666] hover:text-white transition-colors"
              data-testid="merch-show-less-btn">
              SHOW LESS
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
